import { store } from "../store";

export const moderateScale = (size, factor = 1) => {
  let appearanceData = store.getState().appearance.appearance;

  store.subscribe(() => {
    const appearanceUpdatedData = store.getState().appearance.appearance;
    appearanceData = appearanceUpdatedData;
  });

  if (appearanceData && appearanceData?.contentScaling) {
    return size * appearanceData?.contentScalingSize;
  }

  return size * factor;
};

export const parseColor = (color) => {
  // Assume color is in hex format (e.g., #c4d8e4)
  if (!/^#([0-9A-F]{3}){1,2}$/i.test(color)) return "196, 196, 196"; // default rgb in case of an invalid color

  // Remove the hash at the start if it's there
  color = color.slice(1);

  let r, g, b;
  if (color.length === 3) {
    // 3-digit hex
    r = parseInt(color[0] + color[0], 16);
    g = parseInt(color[1] + color[1], 16);
    b = parseInt(color[2] + color[2], 16);
  } else {
    // 6-digit hex
    r = parseInt(color.slice(0, 2), 16);
    g = parseInt(color.slice(2, 4), 16);
    b = parseInt(color.slice(4), 16);
  }
  return `${r}, ${g}, ${b}`;
};

// helper functions to manage cookies
export const setCookie = (name, value, days) => {
  let expires = "";
  if (days) {
    const date = new Date();
    // days * 24 hours * 60 minutes * 60 seconds * 1000 ms
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    expires = "; expires=" + date.toUTCString();
  }
  document.cookie = name + "=" + (value || "") + expires + "; path=/";
};

export const getCookie = (name) => {
  const nameEQ = name + "=";
  const ca = document.cookie.split(";");
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === " ") c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

/**
 * Detects hidden color code messages and extracts color information
 * Format: &&$HIDDEN_MESSAGE$&COLOR CODE: #00FF00&&
 * @param {string} text - The message text to process
 * @returns {object} - { hasColorCode: boolean, colorCode: string|null, cleanText: string }
 */
export const detectHiddenColorCode = (text) => {
  if (!text || typeof text !== 'string') {
    return { hasColorCode: false, colorCode: null, cleanText: text || '' };
  }

  // Regex to match the hidden color code pattern
  const hiddenColorRegex = /&&\$HIDDEN_MESSAGE\$&COLOR CODE:\s*(#[0-9A-Fa-f]{6}|#[0-9A-Fa-f]{3})&&/g;
  
  const matches = text.match(hiddenColorRegex);
  
  if (matches && matches.length > 0) {
    // Extract the color code from the first match
    const colorMatch = matches[0].match(/COLOR CODE:\s*(#[0-9A-Fa-f]{6}|#[0-9A-Fa-f]{3})/);
    const colorCode = colorMatch ? colorMatch[1] : null;
    
    // Remove all hidden color code patterns from the text
    const cleanText = text.replace(hiddenColorRegex, '').trim();
    
    return {
      hasColorCode: true,
      colorCode: colorCode,
      cleanText: cleanText
    };
  }
  
  return {
    hasColorCode: false,
    colorCode: null,
    cleanText: text
  };
};

/**
 * Processes message content for display and speech
 * Removes hidden color codes and returns clean text with color information
 * @param {string} messageContent - The message content to process
 * @returns {object} - { displayText: string, speechText: string, backgroundColor: string|null }
 */
export const processMessageContent = (messageContent) => {
  const { hasColorCode, colorCode, cleanText } = detectHiddenColorCode(messageContent);
  // console.log("hasColorCode", hasColorCode);
  // console.log("colorCode", colorCode);
  // console.log("cleanText", cleanText);
  
  return {
    displayText: cleanText,
    speechText: cleanText, // Same as display text since we remove hidden codes
    backgroundColor: hasColorCode ? colorCode : null
  };
};

/**
 * Applies custom background color with opacity support
 * @param {string} baseColor - The base color from appearance settings
 * @param {string} customColor - The custom color from hidden message (if any)
 * @param {number} opacity - The opacity value (0-100)
 * @returns {string} - CSS background color string
 */
export const getCustomBackgroundColor = (baseColor, customColor, opacity = 100) => {
  // If there's a custom color from hidden message, use it
  if (customColor) {
    return `rgba(${parseColor(customColor)}, ${String(opacity / 100)})`;
  }
  
  // Otherwise, fallback to the original appearance logic
  return `rgba(${parseColor(baseColor)}, ${String(opacity / 100)})`;
};
